import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { PolarArea } from 'react-chartjs-2';
import { Helmet } from 'react-helmet';
import {
  Chart as ChartJS,
  RadialLinearScale,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  RadialLinearScale,
  ArcElement,
  Tooltip,
  Legend
);

const InterquartileRangeCalculator = () => {
  const [data, setData] = useState('');
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    setData(e.target.value);
    setError('');
  };

  const calculateIQR = () => {
    const numbers = data.split(',').map(Number).filter(num => !isNaN(num));

    if (numbers.length === 0) {
      setError('Please enter a valid list of numbers');
      return;
    }

    const sorted = [...numbers].sort((a, b) => a - b);
    const q1 = calculatePercentile(sorted, 25);
    const q3 = calculatePercentile(sorted, 75);
    const iqr = q3 - q1;

    const calculationSteps = [
      'Step 1: Prepare the values',
      `Numbers: ${numbers.join(', ')}`,
      '\nStep 2: Sort the numbers',
      `Sorted Numbers: ${sorted.join(', ')}`,
      '\nStep 3: Calculate Quartiles',
      `Q1 (25th percentile) = ${q1}`,
      `Q3 (75th percentile) = ${q3}`,
      '\nStep 4: Calculate Interquartile Range (IQR)',
      `IQR = Q3 - Q1 = ${q3} - ${q1} = ${iqr}`
    ];

    const newResults = {
      q1,
      q3,
      iqr,
      calculationSteps
    };

    setResults(newResults);

    // Update chart data
    setChartData({
      polarArea: {
        labels: ['Q1', 'Q3', 'IQR'],
        datasets: [{
          label: 'Interquartile Range',
          data: [q1, q3, iqr],
          backgroundColor: [
            'rgba(255, 99, 132, 0.5)',
            'rgba(54, 162, 235, 0.5)',
            'rgba(255, 206, 86, 0.5)'
          ],
          borderColor: [
            'rgba(255, 99, 132, 1)',
            'rgba(54, 162, 235, 1)',
            'rgba(255, 206, 86, 1)'
          ],
          borderWidth: 2
        }]
      }
    });
  };

  const calculatePercentile = (sorted, percentile) => {
    const index = (percentile / 100) * (sorted.length - 1);
    if (Math.floor(index) === index) {
      return sorted[index];
    } else {
      const lower = sorted[Math.floor(index)];
      const upper = sorted[Math.ceil(index)];
      return lower + (upper - lower) * (index - Math.floor(index));
    }
  };

  const exampleData = {
    numbers: [1, 2, 2, 3, 4],
    q1: 1.75,
    q3: 3.25,
    iqr: 1.5
  };

  return (
    <>
      <Helmet>
        {/* Basic SEO Meta Tags */}
        <title>Interquartile Range Calculator - Statistics</title>
        <meta name="description" content="Calculate the interquartile range of a list of numbers. Use our interactive calculator to estimate Q1, Q3, and IQR." />
        <meta name="keywords" content="interquartile range calculator, IQR calculator, statistics calculator, data analysis" />
        <meta name="author" content="Your Company Name" />
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        
        {/* Open Graph Meta Tags for Facebook and LinkedIn */}
        <meta property="og:title" content="Interquartile Range Calculator - Statistics" />
        <meta property="og:description" content="Calculate the interquartile range of a list of numbers. Use our interactive calculator to estimate Q1, Q3, and IQR." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://yourdomain.com/iqr-calculator" />
        <meta property="og:image" content="https://yourdomain.com/iqr-calculator-preview.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta property="og:site_name" content="Statistics Calculators" />
        <meta property="og:locale" content="en_US" />
        
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@your_twitter_handle" />
        <meta name="twitter:creator" content="@your_twitter_handle" />
        <meta name="twitter:title" content="Interquartile Range Calculator - Statistics" />
        <meta name="twitter:description" content="Calculate the interquartile range of a list of numbers. Use our interactive calculator to estimate Q1, Q3, and IQR." />
        <meta name="twitter:image" content="https://yourdomain.com/iqr-calculator-preview.jpg" />
        <meta name="twitter:image:alt" content="Interquartile Range Calculator Preview" />
        
        {/* Pinterest Meta Tags */}
        <meta name="pinterest" content="nopin" />
        <meta name="pinterest-rich-pin" content="true" />
        
        {/* Schema.org Markup for Google+ */}
        <meta itemprop="name" content="Interquartile Range Calculator - Statistics" />
        <meta itemprop="description" content="Calculate the interquartile range of a list of numbers. Use our interactive calculator to estimate Q1, Q3, and IQR." />
        <meta itemprop="image" content="https://yourdomain.com/iqr-calculator-preview.jpg" />
        
        {/* Additional Meta Tags for Better Sharing */}
        <meta name="theme-color" content="#ffffff" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="apple-mobile-web-app-title" content="Interquartile Range Calculator" />
      </Helmet>

      <div className="statistics-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="calculator-header mb-12">
          <div className="flex items-center gap-4">
            <div className="header-icon text-4xl">📊</div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Interquartile Range Calculator</h1>
              <p className="text-lg text-blue-600 font-semibold">Statistics Calculator</p>
            </div>
          </div>
        </div>

        <div className="calculator-description space-y-4 mb-12">
          <p className="text-gray-700">
            Calculate the interquartile range of a list of numbers. This calculator helps you estimate Q1, Q3, and IQR for your data set.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
        >
          <div className="input-group mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="data">
              Data (comma-separated):
            </label>
            <input
              type="text"
              id="data"
              name="data"
              value={data}
              onChange={handleInputChange}
              className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
              placeholder="Enter numbers separated by commas"
            />
          </div>

          <button
            onClick={calculateIQR}
            className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
          >
            Calculate IQR
          </button>

          {error && (
            <div className="text-red-500 text-center mt-4">
              {error}
            </div>
          )}

          {results && (
            <div className="results-section mt-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Results</h3>
              
              <div className="grid grid-cols-1 gap-8 mb-12">
                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h4 className="text-xl font-semibold mb-4">Interquartile Range</h4>
                  <div className="space-y-2">
                    <p className="flex justify-between">
                      <span>Q1</span>
                      <span className="font-medium">{results.q1}</span>
                    </p>
                    <p className="flex justify-between">
                      <span>Q3</span>
                      <span className="font-medium">{results.q3}</span>
                    </p>
                    <p className="flex justify-between">
                      <span>IQR</span>
                      <span className="font-medium">{results.iqr}</span>
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-12 mt-12">
                <div>
                  <h4 className="text-xl font-semibold mb-4">Polar Area Chart</h4>
                  <div className="h-96">
                    <PolarArea
                      data={chartData.polarArea}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          r: {
                            ticks: {
                              display: false
                            }
                          }
                        },
                        plugins: {
                          title: {
                            display: true,
                            text: 'Interquartile Range'
                          },
                          legend: {
                            display: true,
                            position: 'bottom'
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="mt-12 bg-gray-50 p-6 rounded-xl">
                <h4 className="text-xl font-semibold mb-4">Calculation Steps</h4>
                <div className="space-y-4">
                  {results.calculationSteps.map((step, index) => (
                    <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                      <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </motion.div>

        <div className="calculator-section mt-24">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            Example Calculation <span className="ml-2 text-2xl">📝</span>
          </h2>
          <div className="bg-white p-8 rounded-xl shadow-md">
            <p className="text-gray-700 mb-6">
              Here's an example calculation for a data set [1, 2, 2, 3, 4]:
            </p>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-semibold mb-4">Sample Data:</h4>
              <ul className="list-disc pl-8 space-y-2">
                <li>Numbers: {exampleData.numbers.join(', ')}</li>
                <li>Q1: {exampleData.q1}</li>
                <li>Q3: {exampleData.q3}</li>
                <li>IQR: {exampleData.iqr}</li>
              </ul>
              
              <h4 className="font-semibold mt-6 mb-4">Calculations:</h4>
              <ol className="list-decimal pl-8 space-y-4">
                <li>
                  <p>Calculate Q1:</p>
                  <ul className="list-none pl-8 space-y-2">
                    <li>Q1 (25th percentile) = {exampleData.q1}</li>
                  </ul>
                </li>
                <li>
                  <p>Calculate Q3:</p>
                  <ul className="list-none pl-8 space-y-2">
                    <li>Q3 (75th percentile) = {exampleData.q3}</li>
                  </ul>
                </li>
                <li>
                  <p>Calculate IQR:</p>
                  <ul className="list-none pl-8 space-y-2">
                    <li>IQR = Q3 - Q1 = {exampleData.q3} - {exampleData.q1} = {exampleData.iqr}</li>
                  </ul>
                </li>
              </ol>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-24">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            How to Use Interquartile Range Calculator <span className="ml-2 text-2xl">📋</span>
          </h2>
          <div className="bg-white p-8 rounded-xl shadow-md">
            <p className="text-gray-700 mb-6">
              Follow these steps to use the Interquartile Range Calculator effectively:
            </p>
            
            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Gather Data</h3>
                <ul className="list-disc pl-8 space-y-2">
                  <li>Collect the data set you want to analyze</li>
                  <li>Ensure all data points are accurate</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Enter Data</h3>
                <ul className="list-disc pl-8 space-y-2">
                  <li>Input the data set as comma-separated values</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Analyze Results</h3>
                <ul className="list-disc pl-8 space-y-2">
                  <li>Review the calculated quartiles and IQR</li>
                  <li>Use the polar area chart for visual analysis</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-24">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            Frequently Asked Questions <span className="ml-2 text-2xl">❓</span>
          </h2>
          <div className="bg-white p-8 rounded-xl shadow-md">
            <div className="space-y-6">
              <div className="faq-item">
                <h3 className="text-lg font-semibold mb-2">What is the formula to calculate quartiles?</h3>
                <p className="text-gray-600">
                  Quartiles are calculated based on the percentile position in a sorted data set. Q1 is the 25th percentile, Q2 (or median) is the 50th percentile, and Q3 is the 75th percentile.
                </p>
              </div>

              <div className="faq-item">
                <h3 className="text-lg font-semibold mb-2">What is the formula to calculate IQR?</h3>
                <p className="text-gray-600">
                  The formula to calculate IQR is: IQR = Q3 - Q1
                </p>
              </div>

              <div className="faq-item">
                <h3 className="text-lg font-semibold mb-2">Why is it important to calculate IQR accurately?</h3>
                <p className="text-gray-600">
                  Accurate calculation of IQR is important because it:
                  <ul className="list-disc pl-6 mt-2">
                    <li>Provides insights into the spread and variability of the data set</li>
                    <li>Helps in identifying outliers and understanding data distribution</li>
                    <li>Allows for better data analysis and decision making</li>
                  </ul>
                </p>
              </div>

              <div className="faq-item">
                <h3 className="text-lg font-semibold mb-2">What if I don't have a complete data set?</h3>
                <p className="text-gray-600">
                  If you don't have a complete data set, you can:
                  <ol className="list-decimal pl-6 mt-2">
                    <li>Use the available data to calculate estimations</li>
                    <li>Consult with a data analyst for better insights</li>
                    <li>Collect more data points to improve accuracy</li>
                  </ol>
                </p>
              </div>

              <div className="faq-item">
                <h3 className="text-lg font-semibold mb-2">Can I use this calculator for different types of data?</h3>
                <p className="text-gray-600">
                  Yes, this calculator can be used for different types of numerical data as long as you have the values in a comma-separated format.
                </p>
              </div>

              <div className="faq-item">
                <h3 className="text-lg font-semibold mb-2">Why are my results different from expected?</h3>
                <p className="text-gray-600">
                If your results seem unexpected, check:
                  <ul className="list-disc pl-6 mt-2">
                    <li>If you've entered the correct numbers</li>
                    <li>If your data set is complete</li>
                    <li>If all values are in the correct format</li>
                  </ul>
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-24">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            Features & Benefits <span className="ml-2 text-2xl">✨</span>
          </h2>
          <div className="bg-white p-8 rounded-xl shadow-md">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="text-center p-6 bg-blue-50 rounded-lg">
                <div className="text-4xl mb-4">📊</div>
                <h3 className="text-lg font-semibold mb-2">Visual Analysis</h3>
                <p className="text-gray-600">Interactive polar area charts for easy visualization of IQR calculations</p>
              </div>
              <div className="text-center p-6 bg-green-50 rounded-lg">
                <div className="text-4xl mb-4">🎯</div>
                <h3 className="text-lg font-semibold mb-2">Precise Calculations</h3>
                <p className="text-gray-600">Accurate Q1, Q3, and IQR calculations with step-by-step breakdown</p>
              </div>
              <div className="text-center p-6 bg-yellow-50 rounded-lg">
                <div className="text-4xl mb-4">🔄</div>
                <h3 className="text-lg font-semibold mb-2">Unit Conversion</h3>
                <p className="text-gray-600">Automatic handling of data format conversions</p>
              </div>
              <div className="text-center p-6 bg-purple-50 rounded-lg">
                <div className="text-4xl mb-4">📚</div>
                <h3 className="text-lg font-semibold mb-2">Educational Value</h3>
                <p className="text-gray-600">Detailed explanations of IQR concepts</p>
              </div>
              <div className="text-center p-6 bg-pink-50 rounded-lg">
                <div className="text-4xl mb-4">⚙️</div>
                <h3 className="text-lg font-semibold mb-2">User-Friendly</h3>
                <p className="text-gray-600">Intuitive interface with clear input fields</p>
              </div>
              <div className="text-center p-6 bg-orange-50 rounded-lg">
                <div className="text-4xl mb-4">🔄</div>
                <h3 className="text-lg font-semibold mb-2">Real-time Updates</h3>
                <p className="text-gray-600">Instant calculation results as you type</p>
              </div>
            </div>

            <div className="mt-12">
              <h3 className="text-xl font-semibold mb-4">Key Benefits</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Save time with automatic calculations</li>
                <li>Reduce calculation errors with built-in validation</li>
                <li>Learn IQR concepts through practical examples</li>
                <li>Accessible from any device</li>
                <li>Free to use for educational and professional purposes</li>
                <li>Perfect for students, educators, and data analysts</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-24">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            Understanding Interquartile Range <span className="ml-2 text-2xl">📊</span>
          </h2>
          <div className="bg-white p-8 rounded-xl shadow-md">
            <p className="text-gray-700 mb-6">
              Calculating the interquartile range is essential for understanding the spread and variability of a data set. Here's why it's important:
            </p>
            <ul className="list-none pl-8 space-y-4">
              <li className="flex items-center">
                <span className="h-3 w-3 bg-blue-600 rounded-full mr-2"></span>
                <span className="font-medium">Accurate Data Analysis:</span> Ensuring you get meaningful insights from your data
              </li>
              <li className="flex items-center">
                <span className="h-3 w-3 bg-green-600 rounded-full mr-2"></span>
                <span className="font-medium">Cost Efficiency:</span> Avoiding costly mistakes by understanding your data correctly
              </li>
              <li className="flex items-center">
                <span className="h-3 w-3 bg-yellow-600 rounded-full mr-2"></span>
                <span className="font-medium">Project Management:</span> Planning and executing data-driven projects effectively
              </li>
              <li className="flex items-center">
                <span className="h-3 w-3 bg-red-600 rounded-full mr-2"></span>
                <span className="font-medium">Decision Making:</span> Making informed decisions based on data insights
              </li>
            </ul>
            <p className="italic text-gray-500 mt-6">
              Note: Always double-check your data and calculations to ensure accuracy and avoid misleading results.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default InterquartileRangeCalculator;
